import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { EditorialNumber } from "./EditorialNumber";

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.8, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="services"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40 overflow-hidden"
      data-testid="services-section"
    >
      {/* Ambient background — luxury villa hallway, evocative of order + method */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1721852474658-884e1881fe31?auto=format&fit=crop&w=2400&q=90"
          alt=""
          className="w-full h-full object-cover object-center bg-ken-burns"
          style={{ filter: "contrast(0.98) brightness(0.98) saturate(0.94)" }}
        />
        {/* Stronger ivory tint to guarantee text readability */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(241,238,232,0.86) 0%, rgba(241,238,232,0.82) 45%, rgba(241,238,232,0.90) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(241,238,232,0.0) 40%, rgba(35,35,38,0.14) 100%)",
          }}
        />
      </div>

      <EditorialNumber n="03" variant="dark" align="right" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#0F0E0C]/70 text-[0.7rem] tracking-[0.28em] uppercase font-medium">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.services.overline}</span>
            </div>
          </motion.div>

          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line text-[#0F0E0C]"
            >
              {t.services.title}
            </motion.h2>
            {t.services.lead && (
              <motion.p
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={revealUp}
                custom={2}
                className="text-[#0F0E0C]/85 text-lg leading-relaxed max-w-2xl mt-6 font-medium"
              >
                {t.services.lead}
              </motion.p>
            )}
          </div>
        </div>

        <div className="border-t-2 border-[#232326]/25">
          {t.services.items.map((s, i) => (
            <motion.a
              key={s.k}
              href="#contact"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={revealUp}
              custom={i}
              className="group relative grid grid-cols-12 gap-4 md:gap-12 py-10 md:py-14 md:pl-6 lg:pl-10 border-b-2 border-[#232326]/25 transition-all duration-500 hover:bg-[#13535A] overflow-hidden"
              data-testid={`service-${s.k.replace(/\s/g, "-")}`}
            >
              {/* Left brass accent bar that slides in on hover */}
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-1 bg-[#8A6F45] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out"
              />
              <div className="col-span-10 md:col-span-5 md:pr-6">
                <h3 className="font-display text-2xl md:text-[2.6rem] tracking-tight leading-[1.05] text-[#0A0908] group-hover:text-[#F1EEE8] transition-colors duration-500">
                  {s.title}
                </h3>
              </div>
              <div className="col-span-10 col-start-3 md:col-span-6 md:col-start-auto text-[#0A0908]/90 group-hover:text-[#F1EEE8]/85 leading-[1.7] pt-2 text-[1.08rem] md:text-[1.15rem] font-normal transition-colors duration-500">
                {s.body}
              </div>
              <div className="col-span-2 md:col-span-1 flex items-start justify-end pt-2 pr-2 md:pr-4">
                <motion.span
                  whileHover={{ x: 4, y: -4 }}
                  className="text-[#0A0908]/80 group-hover:text-[#8A6F45] transition-colors duration-500"
                >
                  <ArrowUpRight size={26} weight="light" />
                </motion.span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
